import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { alpha, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const CHROME_H = 220;
const CHROME_W = 88;
const MAX_VW = 0.92;
const MAX_VH = 0.92;
const TARGET_PAGE_W = 680;
const PREFETCH_MARGIN = '240px 0px';

function buildPageText(items) {
    let full = '';
    const ranges = [];
    (items || []).forEach((item, idx) => {
        const start = full.length;
        full += item.str;
        ranges.push({ start, end: full.length, idx });
        full += ' ';
    });
    return { full, ranges };
}

function buildPageMatches(items, query) {
    if (!query || !items) return [];
    const q = query.toLocaleLowerCase();
    const { full, ranges } = buildPageText(items);
    const lower = full.toLocaleLowerCase();
    const matches = [];
    let i = lower.indexOf(q);
    while (i !== -1) {
        const matchStart = i;
        const matchEnd = i + q.length;
        const itemIndices = [];
        ranges.forEach((range) => {
            if (range.end <= matchStart || range.start >= matchEnd) return;
            itemIndices.push(range.idx);
        });
        matches.push({ itemIndices });
        i = lower.indexOf(q, i + q.length);
    }
    return matches;
}

function markTextDivs(textDivs, items, query, activeItemIndices) {
    if (!textDivs) return;
    textDivs.forEach((div) => {
        div.classList.remove('archive-hl');
        div.classList.remove('archive-hl-active');
    });
    if (!query) return;
    const q = query.toLocaleLowerCase();
    const { full, ranges } = buildPageText(items);
    const lower = full.toLocaleLowerCase();
    let i = lower.indexOf(q);
    while (i !== -1) {
        const matchStart = i;
        const matchEnd = i + q.length;
        ranges.forEach((range) => {
            if (range.end <= matchStart || range.start >= matchEnd) return;
            if (textDivs[range.idx]) textDivs[range.idx].classList.add('archive-hl');
        });
        i = lower.indexOf(q, i + q.length);
    }
    (activeItemIndices || []).forEach((idx) => {
        if (textDivs[idx]) textDivs[idx].classList.add('archive-hl-active');
    });
}

function PdfPreviewDialog({ open, onClose, url, title, pageNumber, searchText = '', actions }) {
    const theme = useTheme();
    const scrollRef = useRef(null);
    const pageWrapRefs = useRef([]);
    const pageMetaRef = useRef([]);
    const renderGenRef = useRef(0);
    const pdfDocRef = useRef(null);
    const scaleRef = useRef(1);
    const queryRef = useRef('');
    const numPagesRef = useRef(0);
    const currentPageRef = useRef(1);
    const matchesRef = useRef([]);
    const currentMatchIndexRef = useRef(-1);
    const initialJumpRef = useRef(true);
    const apiRef = useRef({});
    const [pdfDoc, setPdfDoc] = useState(null);
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [inputValue, setInputValue] = useState('');
    const [query, setQuery] = useState('');
    const [pagesReady, setPagesReady] = useState(false);
    const [fitScale, setFitScale] = useState(1);
    const [pageSize, setPageSize] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [matches, setMatches] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
    const [matchesReady, setMatchesReady] = useState(false);

    const [viewW, setViewW] = useState(0);

    const chromeH = actions ? 260 : CHROME_H;
    const maxDialogW = window.innerWidth * MAX_VW;
    const maxDialogH = window.innerHeight * MAX_VH;
    const scale = fitScale * zoom;
    const pageW = pageSize ? pageSize.width * scale : 0;
    const pageH = pageSize ? pageSize.height * scale : 0;
    const baseDialogW = Math.min(TARGET_PAGE_W + CHROME_W, maxDialogW);
    const dialogW = zoom > 1 && pageSize
        ? Math.min(Math.max(baseDialogW, pageW + CHROME_W), maxDialogW)
        : baseDialogW;
    const dialogH = pageSize ? Math.min(pageH + chromeH, maxDialogH) : maxDialogH;
    const atMaxSize = pageSize && (pageW > viewW || pageH + chromeH > maxDialogH);
    const contentOverflow = atMaxSize || numPages > 1 ? 'auto' : 'hidden';

    pdfDocRef.current = pdfDoc;
    scaleRef.current = scale;
    queryRef.current = query;
    numPagesRef.current = numPages;
    currentPageRef.current = currentPage;
    matchesRef.current = matches;
    currentMatchIndexRef.current = currentMatchIndex;

    const activeIndicesForPage = (n) => {
        const list = matchesRef.current;
        const idx = currentMatchIndexRef.current;
        if (idx < 0 || !list[idx] || list[idx].page !== n) return null;
        return list[idx].itemIndices;
    };

    const scrollToPage = (n) => {
        const wrap = pageWrapRefs.current[n];
        if (wrap) wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const scrollToMatchEl = (match, smooth = true) => {
        const meta = pageMetaRef.current[match.page];
        const el = meta?.textDivs?.[match.itemIndices?.[0]];
        if (el) {
            el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'center' });
            return;
        }
        scrollToPage(match.page);
    };

    const ensureCanvas = async (n) => {
        const pdf = pdfDocRef.current;
        const wrap = pageWrapRefs.current[n];
        if (!pdf || !wrap) return;
        const meta = pageMetaRef.current[n] || (pageMetaRef.current[n] = {});

        const work = async () => {
            const s = scaleRef.current;
            if (meta.canvasScale === s && wrap.querySelector('canvas')?.width) return;

            if (meta.renderTask) {
                meta.renderTask.cancel();
                try {
                    await meta.renderTask.promise;
                } catch (e) {
                    if (e?.name !== 'RenderingCancelledException') throw e;
                }
                meta.renderTask = null;
            }

            const gen = renderGenRef.current;
            const page = await pdf.getPage(n);
            if (gen !== renderGenRef.current) return;
            const sNow = scaleRef.current;
            if (meta.canvasScale === sNow && wrap.querySelector('canvas')?.width) return;

            const viewport = page.getViewport({ scale: sNow });
            const canvas = wrap.querySelector('canvas');
            if (!canvas) return;
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            canvas.style.width = `${viewport.width}px`;
            canvas.style.height = `${viewport.height}px`;
            wrap.style.width = `${viewport.width}px`;
            wrap.style.height = `${viewport.height}px`;
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);

            const task = page.render({ canvasContext: context, viewport });
            meta.renderTask = task;
            try {
                await task.promise;
            } catch (e) {
                if (e?.name === 'RenderingCancelledException') return;
                throw e;
            } finally {
                if (meta.renderTask === task) meta.renderTask = null;
            }
            if (gen !== renderGenRef.current) return;
            meta.canvasScale = sNow;
        };

        const queued = (meta.canvasBusy || Promise.resolve()).then(work, work);
        meta.canvasBusy = queued;
        try {
            await queued;
        } finally {
            if (meta.canvasBusy === queued) meta.canvasBusy = null;
        }
    };

    const ensureTextItems = async (n) => {
        const pdf = pdfDocRef.current;
        if (!pdf) return [];
        const meta = pageMetaRef.current[n] || (pageMetaRef.current[n] = {});
        if (meta.items) return meta.items;
        if (meta.itemsBusy) {
            await meta.itemsBusy;
            return meta.items || [];
        }
        meta.itemsBusy = (async () => {
            const page = await pdf.getPage(n);
            const textContent = await page.getTextContent();
            meta.textContent = textContent;
            meta.items = textContent.items;
        })();
        try {
            await meta.itemsBusy;
        } finally {
            meta.itemsBusy = null;
        }
        return meta.items || [];
    };

    const ensureTextLayer = async (n) => {
        await ensureCanvas(n);
        const items = await ensureTextItems(n);
        const wrap = pageWrapRefs.current[n];
        const pdf = pdfDocRef.current;
        const s = scaleRef.current;
        if (!wrap || !pdf) return;
        const meta = pageMetaRef.current[n] || (pageMetaRef.current[n] = {});
        if (meta.textScale === s && meta.textDivs?.length) {
            markTextDivs(meta.textDivs, items, queryRef.current, activeIndicesForPage(n));
            return;
        }
        const gen = renderGenRef.current;
        const page = await pdf.getPage(n);
        if (gen !== renderGenRef.current) return;
        const viewport = page.getViewport({ scale: s });
        const textLayerDiv = wrap.querySelector('.textLayer');
        if (!textLayerDiv || !meta.textContent) return;
        textLayerDiv.innerHTML = '';
        textLayerDiv.style.width = `${viewport.width}px`;
        textLayerDiv.style.height = `${viewport.height}px`;
        textLayerDiv.style.setProperty('--scale-factor', s);
        const textDivs = [];
        await pdfjsLib.renderTextLayer({
            textContentSource: meta.textContent,
            container: textLayerDiv,
            viewport,
            textDivs
        }).promise;
        if (gen !== renderGenRef.current) return;
        meta.textDivs = textDivs;
        meta.textScale = s;
        markTextDivs(textDivs, items, queryRef.current, activeIndicesForPage(n));
    };

    const goToMatch = async (index, smooth = true) => {
        const list = matchesRef.current;
        if (index < 0 || index >= list.length) return;
        const match = list[index];
        setCurrentMatchIndex(index);
        currentMatchIndexRef.current = index;
        setCurrentPage(match.page);
        currentPageRef.current = match.page;
        await apiRef.current.ensureCanvas(match.page);
        await apiRef.current.ensureTextLayer(match.page);
        pageMetaRef.current.forEach((meta, page) => {
            if (!meta?.textDivs) return;
            const active = page === match.page ? match.itemIndices : null;
            markTextDivs(meta.textDivs, meta.items, queryRef.current, active);
        });
        scrollToMatchEl(match, smooth);
    };

    apiRef.current = { ensureCanvas, ensureTextItems, ensureTextLayer, goToMatch };

    const renderNearby = async () => {
        const scrollEl = scrollRef.current;
        const total = numPagesRef.current;
        if (!scrollEl || !total) return;
        const rootRect = scrollEl.getBoundingClientRect();
        const pad = 240;
        const jobs = [];
        for (let n = 1; n <= total; n++) {
            const wrap = pageWrapRefs.current[n];
            if (!wrap) continue;
            const r = wrap.getBoundingClientRect();
            if (r.bottom > rootRect.top - pad && r.top < rootRect.bottom + pad) {
                jobs.push(apiRef.current.ensureCanvas(n).then(() => {
                    if (queryRef.current) return apiRef.current.ensureTextLayer(n);
                    return null;
                }));
            }
        }
        await Promise.all(jobs);
    };

    useEffect(() => {
        if (!open) return;
        setInputValue(searchText);
        setQuery(searchText);
        setCurrentPage(pageNumber ? Number(pageNumber) : 1);
        setPagesReady(false);
        setZoom(1);
        setMatches([]);
        setCurrentMatchIndex(-1);
        setMatchesReady(false);
        initialJumpRef.current = true;
        matchesRef.current = [];
        currentMatchIndexRef.current = -1;
        setViewW(0);
    }, [open, url, pageNumber, searchText]);

    useEffect(() => {
        if (!open || !url) {
            setPdfDoc(null);
            setNumPages(0);
            setPageSize(null);
            pageMetaRef.current = [];
            pdfDocRef.current = null;
            return;
        }
        let cancelled = false;
        pageMetaRef.current = [];
        pdfjsLib.getDocument(url).promise.then(async (pdf) => {
            if (cancelled) return;
            const page = await pdf.getPage(1);
            const unscaled = page.getViewport({ scale: 1 });
            setPageSize({ width: unscaled.width, height: unscaled.height });
            setPdfDoc(pdf);
            setNumPages(pdf.numPages);
        });
        return () => {
            cancelled = true;
        };
    }, [open, url]);

    useEffect(() => {
        if (!open) return;
        const el = scrollRef.current;
        if (!el) return;
        const update = () => setViewW(el.clientWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, [open, pdfDoc, numPages]);

    useEffect(() => {
        if (!open || !pageSize || viewW <= 0 || zoom !== 1) return;
        const next = viewW / pageSize.width;
        setFitScale((prev) => (Math.abs(prev - next) < 0.005 ? prev : next));
    }, [open, pageSize, viewW, zoom]);

    useEffect(() => {
        if (!open || !pdfDoc || numPages === 0 || !pageSize || viewW <= 0) return;
        renderGenRef.current += 1;
        const gen = renderGenRef.current;
        pageMetaRef.current.forEach((meta) => {
            if (!meta) return;
            meta.canvasScale = null;
            meta.textScale = null;
            meta.textDivs = [];
        });

        let cancelled = false;
        const first = currentPageRef.current || 1;

        const start = async () => {
            await apiRef.current.ensureCanvas(first);
            if (cancelled || gen !== renderGenRef.current) return;
            setPagesReady(true);
            await renderNearby();
            if (cancelled || gen !== renderGenRef.current) return;
            const matchIdx = currentMatchIndexRef.current;
            if (queryRef.current.trim() && matchIdx >= 0) {
                await apiRef.current.goToMatch(matchIdx, false);
            }
            if (cancelled || gen !== renderGenRef.current) return;
            for (let n = 1; n <= numPages; n++) {
                if (cancelled || gen !== renderGenRef.current) return;
                await apiRef.current.ensureTextItems(n);
            }
        };
        start();

        const scrollEl = scrollRef.current;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                const n = Number(entry.target.dataset.page);
                if (!n) return;
                apiRef.current.ensureCanvas(n).then(() => {
                    if (queryRef.current) return apiRef.current.ensureTextLayer(n);
                    return null;
                });
            });
        }, { root: scrollEl, rootMargin: PREFETCH_MARGIN, threshold: 0.01 });

        for (let n = 1; n <= numPages; n++) {
            const wrap = pageWrapRefs.current[n];
            if (wrap) observer.observe(wrap);
        }

        return () => {
            cancelled = true;
            observer.disconnect();
            pageMetaRef.current.forEach((meta) => {
                if (!meta?.renderTask) return;
                meta.renderTask.cancel();
            });
        };
    }, [open, pdfDoc, numPages, pageSize, fitScale, zoom]);

    useEffect(() => {
        if (!pagesReady || !open) return;
        if (queryRef.current.trim()) return;
        scrollToPage(currentPageRef.current);
    }, [pagesReady, open, url]);

    useEffect(() => {
        if (!open || !pagesReady || !pdfDoc) return;
        let cancelled = false;
        const run = async () => {
            const q = (query || '').trim();
            if (!q) {
                setMatches([]);
                matchesRef.current = [];
                setCurrentMatchIndex(-1);
                currentMatchIndexRef.current = -1;
                setMatchesReady(true);
                return;
            }
            setMatchesReady(false);
            const list = [];
            for (let n = 1; n <= numPagesRef.current; n++) {
                if (cancelled) return;
                const items = await apiRef.current.ensureTextItems(n);
                buildPageMatches(items, q).forEach((m) => {
                    list.push({ page: n, itemIndices: m.itemIndices });
                });
            }
            if (cancelled) return;
            let idx = 0;
            if (initialJumpRef.current) {
                initialJumpRef.current = false;
                const targetPage = pageNumber ? Number(pageNumber) : 0;
                if (targetPage) {
                    const onPage = list.findIndex((m) => m.page === targetPage);
                    if (onPage !== -1) idx = onPage;
                }
            }
            setMatches(list);
            matchesRef.current = list;
            if (!list.length) {
                setCurrentMatchIndex(-1);
                currentMatchIndexRef.current = -1;
                setMatchesReady(true);
                const fallbackPage = pageNumber ? Number(pageNumber) : currentPageRef.current;
                if (fallbackPage) scrollToPage(fallbackPage);
                return;
            }
            setCurrentMatchIndex(idx);
            currentMatchIndexRef.current = idx;
            setMatchesReady(true);
            await apiRef.current.goToMatch(idx);
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [open, pagesReady, pdfDoc, query, pageNumber]);

    const handleSearch = async () => {
        setQuery(inputValue);
        if (!inputValue.trim()) {
            pageMetaRef.current.forEach((meta) => {
                if (meta?.textDivs) markTextDivs(meta.textDivs, meta.items, '');
            });
        }
    };

    const matchMode = Boolean(query.trim()) && (!matchesReady || matches.length > 0);

    const goToPage = async (n) => {
        if (n < 1 || n > numPages) return;
        setCurrentPage(n);
        await apiRef.current.ensureCanvas(n);
        if (queryRef.current) await apiRef.current.ensureTextLayer(n);
        scrollToPage(n);
    };

    const handleZoomOut = () => {
        if (zoom <= 0.5) return;
        setZoom(Math.round((zoom - 0.25) * 100) / 100);
    };

    const handleZoomIn = () => {
        if (zoom >= 3) return;
        setZoom(Math.round((zoom + 0.25) * 100) / 100);
    };

    const handleScroll = () => {
        const scrollEl = scrollRef.current;
        if (!scrollEl) return;
        const top = scrollEl.getBoundingClientRect().top;
        let page = 1;
        for (let n = 1; n <= numPages; n++) {
            const wrap = pageWrapRefs.current[n];
            if (!wrap) continue;
            if (wrap.getBoundingClientRect().top - top <= 40) page = n;
        }
        setCurrentPage((prev) => (prev === page ? prev : page));
    };

    const pageList = [];
    for (let n = 1; n <= numPages; n++) pageList.push(n);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            sx={{
                '& .MuiDialog-paper': {
                    width: dialogW,
                    maxWidth: maxDialogW,
                    height: dialogH,
                    maxHeight: maxDialogH,
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width 0.2s ease, height 0.2s ease'
                }
            }}
        >
            <DialogTitle sx={{ pr: 6 }}>
                {title}
                <IconButton
                    aria-label="kapat"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center', flexShrink: 0 }}>
                    <TextField
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                        label="Arama Yap"
                        size="small"
                        sx={{ flexGrow: 1, background: 'white' }}
                    />
                    <Button variant="contained" size="small" onClick={handleSearch}>Ara</Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexShrink: 0 }}>
                    {matchMode ? (
                        <>
                            <Button
                                size="small"
                                disabled={!matchesReady || currentMatchIndex <= 0}
                                onClick={() => goToMatch(currentMatchIndex - 1)}
                            >
                                Önceki
                            </Button>
                            <Typography variant="caption">
                                {matchesReady
                                    ? `Eşleşme ${currentMatchIndex + 1} / ${matches.length}`
                                    : 'Eşleşme …'}
                            </Typography>
                            <Button
                                size="small"
                                disabled={!matchesReady || currentMatchIndex >= matches.length - 1}
                                onClick={() => goToMatch(currentMatchIndex + 1)}
                            >
                                Sonraki
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                Sayfa {currentPage} / {numPages}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Button size="small" disabled={currentPage <= 1} onClick={() => goToPage(currentPage - 1)}>Önceki</Button>
                            <Typography variant="caption">Sayfa {currentPage} / {numPages}</Typography>
                            <Button size="small" disabled={currentPage >= numPages} onClick={() => goToPage(currentPage + 1)}>Sonraki</Button>
                        </>
                    )}
                    <IconButton size="small" aria-label="küçült" disabled={zoom <= 0.5} onClick={handleZoomOut}>
                        <ZoomOutIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
                    <IconButton size="small" aria-label="büyüt" disabled={zoom >= 3} onClick={handleZoomIn}>
                        <ZoomInIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Box
                    ref={scrollRef}
                    onScroll={handleScroll}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        width: '100%',
                        overflow: contentOverflow,
                        border: '1px solid #ddd',
                        bgcolor: '#fafafa',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: pageW && viewW && pageW > viewW + 1 ? 'flex-start' : 'center'
                    }}
                >
                    {pageList.map((n) => (
                        <Box
                            key={n}
                            data-page={n}
                            ref={(el) => { pageWrapRefs.current[n] = el; }}
                            sx={{
                                position: 'relative',
                                mb: 1,
                                width: pageW || 'auto',
                                height: pageH || 'auto',
                                bgcolor: '#fff',
                                '& canvas': {
                                    display: 'block',
                                    backgroundColor: '#fff'
                                },
                                '& .textLayer': {
                                    position: 'absolute',
                                    inset: 0,
                                    overflow: 'hidden',
                                    lineHeight: 1,
                                    zIndex: 2
                                },
                                '& .textLayer span, & .textLayer br': {
                                    color: 'transparent',
                                    position: 'absolute',
                                    whiteSpace: 'pre',
                                    cursor: 'text',
                                    transformOrigin: '0% 0%'
                                },
                                '& .textLayer span.archive-hl': {
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.4)
                                },
                                '& .textLayer span.archive-hl-active': {
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.75)
                                }
                            }}
                        >
                            <canvas />
                            <div className="textLayer" />
                        </Box>
                    ))}
                </Box>
            </DialogContent>
            {actions ? (
                <DialogActions>
                    {actions}
                </DialogActions>
            ) : null}
        </Dialog>
    );
}

export default PdfPreviewDialog;
